import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import {
  buildGlAccountBulkPayload,
  formatChartOfAccountBulkMessage,
  parseSpreadsheetFile,
} from './glAccountImportUtils';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};
const emptyStr = (v) => (v === '' || v == null ? null : v);

const defaultForm = {
  propertyId: '',
  glCode: '',
  glDescription: '',
  glAccount: '',
  glSystem: '',
  accountType: '',
  status: 'ACTIVE',
};

const mapGlAccountRow = (item) => ({
  id: item.ledgerAccountId,
  propertyId: item.propertyId || '',
  glCode: item.glCode,
  glDescription: item.glDescription,
  glAccount: item.glAccount,
  glSystem: item.glSystem,
  accountType: item.accountType,
  status: item.status,
  raw: item,
});

const getPropertyOptionId = (property) => property?.propertyId || property?.businessUnitId || '';

const getPropertyCode = (property) => (
  property?.propertyCode
  || property?.property_code
  || property?.PROPERTY_CODE
  || property?.businessUnitCode
  || property?.customerPropertyId
  || ''
);

const getPropertyName = (property) => (
  property?.propertyName
  || property?.property_name
  || property?.PROPERTY_NAME
  || property?.businessUnitName
  || property?.name
  || ''
);

const getPropertyOptionLabel = (property) => {
  if (!property) return '';
  if (typeof property === 'string') return property;
  const code = String(getPropertyCode(property) || '').trim();
  const name = String(getPropertyName(property) || '').trim();
  if (code && name) return `${code} - ${name}`;
  return name || code || String(getPropertyOptionId(property) || '');
};

const normalizePropertyOption = (property) => {
  if (!property) return property;
  return {
    ...property,
    propertyCode: getPropertyCode(property),
    propertyName: getPropertyName(property) || property.propertyName,
  };
};

const GlAccountMaster = () => {
  const { canAdd, canEdit, canDelete, canUpload } = useFunctionPermissions(FC.GL_ACCOUNT_MASTER);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importReadyRows, setImportReadyRows] = useState([]);
  const [importPropertyId, setImportPropertyId] = useState('');
  const [importDragActive, setImportDragActive] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const selectedImportProperty = useMemo(
    () => properties.find((property) => String(getPropertyOptionId(property)) === String(importPropertyId)) || null,
    [properties, importPropertyId]
  );

  const selectedFormProperty = useMemo(
    () => properties.find((property) => String(getPropertyOptionId(property)) === String(form.propertyId)) || null,
    [properties, form.propertyId]
  );

  const fetchProperties = useCallback(async () => {
    if (!tenantId) {
      setProperties([]);
      return;
    }
    try {
      const properties = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
      const list = Array.isArray(properties) ? properties : [];
      const userId = getUserId();

      if (!userId) {
        setProperties(list.map(normalizePropertyOption));
        return;
      }

      const userProps = await apiFetch(
        `conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${userId}`
      ).catch(() => []);
      const allowedPropertyIds = new Set(
        (Array.isArray(userProps) ? userProps : [])
          .map((item) => item.propertyId)
          .filter(Boolean)
          .map(String)
      );

      // Only properties assigned to the logged-in user (same pattern as AP Hub).
      setProperties(
        (allowedPropertyIds.size
          ? list.filter((property) => property.propertyId && allowedPropertyIds.has(String(property.propertyId)))
          : []
        ).map(normalizePropertyOption)
      );
    } catch (err) {
      setProperties([]);
      console.error('Failed to load properties', err);
    }
  }, [tenantId]);

  const fetchGlAccounts = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/cash-recon/chart-of-account/by-tenant?tenant_id=${tenantId}`);
      setRows((Array.isArray(data) ? data : []).map(mapGlAccountRow));
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load GL accounts');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchGlAccounts();
    fetchProperties();
  }, [fetchGlAccounts, fetchProperties]);

  useEffect(() => {
    if (!importPropertyId) return;
    const stillAllowed = properties.some(
      (property) => String(getPropertyOptionId(property)) === String(importPropertyId)
    );
    if (!stillAllowed) {
      setImportPropertyId('');
      setImportFileName('');
      setImportReadyRows([]);
    }
  }, [properties, importPropertyId]);

  const propertyLabelById = useMemo(() => {
    const map = {};
    properties.forEach((property) => {
      const label = getPropertyOptionLabel(property);
      if (property.propertyId) map[String(property.propertyId)] = label;
      if (property.businessUnitId) map[String(property.businessUnitId)] = label;
      const optionId = getPropertyOptionId(property);
      if (optionId) map[String(optionId)] = label;
    });
    return map;
  }, [properties]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const businessUnit = propertyLabelById[String(row.propertyId || '')] || '';
      return (
        (row.glCode || '').toLowerCase().includes(q)
        || (row.glDescription || '').toLowerCase().includes(q)
        || (row.glAccount || '').toLowerCase().includes(q)
        || (row.glSystem || '').toLowerCase().includes(q)
        || (row.accountType || '').toLowerCase().includes(q)
        || businessUnit.toLowerCase().includes(q)
      );
    });
  }, [rows, searchQuery, propertyLabelById]);

  const columns = useMemo(() => [
    { field: 'glCode', headerName: 'GL Code', width: 140, resizable: true },
    { field: 'glDescription', headerName: 'Description', width: 220, resizable: true },
    { field: 'glAccount', headerName: 'GL Account', width: 160, resizable: true },
    { field: 'glSystem', headerName: 'GL System', width: 120, resizable: true },
    {
      field: 'businessUnit',
      headerName: 'Business Unit',
      width: 200,
      resizable: true,
      valueGetter: (params) => propertyLabelById[String(params.row.propertyId || '')] || '—',
    },
    { field: 'accountType', headerName: 'Account Type', width: 130, resizable: true },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
  ], [propertyLabelById]);

  const buildPayload = () => ({
    tenantId,
    propertyId: emptyStr(form.propertyId),
    glCode: form.glCode?.trim(),
    glDescription: form.glDescription?.trim(),
    glAccount: form.glAccount?.trim(),
    glSystem: form.glSystem?.trim(),
    accountType: emptyStr(form.accountType),
    status: form.status || 'ACTIVE',
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setForm({
      propertyId: selectedRow.propertyId || selectedRow.raw?.propertyId || '',
      glCode: selectedRow.glCode || '',
      glDescription: selectedRow.glDescription || '',
      glAccount: selectedRow.glAccount || '',
      glSystem: selectedRow.glSystem || '',
      accountType: selectedRow.accountType || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/cash-recon/chart-of-account', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId, updatedBy: userId }),
        });
        showSuccess('GL Account Master saved successfully');
      } else {
        await apiFetch(`conductor/cash-recon/chart-of-account/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ ...payload, updatedBy: userId }),
        });
        showSuccess('GL Account Master updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchGlAccounts();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/cash-recon/chart-of-account/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchGlAccounts();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetImportState = () => {
    setImportOpen(false);
    setImportFileName('');
    setImportReadyRows([]);
    setImportPropertyId('');
    setImportDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openImportDialog = () => {
    if (!canUpload) return;
    setImportFileName('');
    setImportReadyRows([]);
    setImportPropertyId('');
    setImportDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setImportOpen(true);
  };

  const processImportFile = async (file) => {
    if (!canUpload || !file) return;
    try {
      setImportLoading(true);
      const parsedRows = await parseSpreadsheetFile(file);
      const payloadRows = buildGlAccountBulkPayload(parsedRows, tenantId, {
        defaultGlSystem: 'MANUAL',
        propertyId: importPropertyId || null,
      });
      setImportFileName(file.name);
      setImportReadyRows(payloadRows);
      if (!payloadRows.length) {
        showError('No valid rows found. Each row needs at least GL Code and Description.');
      }
    } catch (err) {
      showError(err.message || 'Failed to read import file');
      setImportFileName('');
      setImportReadyRows([]);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportFileSelected = async (event) => {
    if (!canUpload) return;
    const file = event.target.files?.[0];
    await processImportFile(file);
  };

  const handleImportDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setImportDragActive(false);
    if (!canUpload || importLoading) return;
    const file = event.dataTransfer.files?.[0];
    await processImportFile(file);
  };

  const clearSelectedImportFile = () => {
    setImportFileName('');
    setImportReadyRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirmImport = async () => {
    if (!canUpload) return;
    if (!importReadyRows.length) return;
    try {
      setImportLoading(true);
      const payloadRows = importReadyRows.map((row) => ({
        ...row,
        propertyId: importPropertyId || null,
      }));
      const response = await apiFetch('conductor/cash-recon/chart-of-account/bulk', {
        method: 'POST',
        body: JSON.stringify(payloadRows),
      });
      showSuccess(formatChartOfAccountBulkMessage(response));
      resetImportState();
      fetchGlAccounts();
    } catch (err) {
      showError(err.message || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>GL Account Master</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search gl accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            sx={{ ...harmonatePageSearchFieldSx, width: 280 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={harmonatePageSearchIconSx} /></InputAdornment>,
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} edge="end" sx={{ color: ht.white }}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleImportFileSelected} />
          <Tooltip title={canUpload ? 'Import GL Account Master from CSV/Excel' : 'No upload permission'}>
            <span>
              <IconButton onClick={openImportDialog} disabled={importLoading || !canUpload} sx={harmonatePlainIconButtonSx}>
                <UploadFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit GL Account Master' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add GL Account Master' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete GL Account Master' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro rows={filteredRows} columns={columns} loading={loading} rowHeight={40} pagination pageSizeOptions={[10, 25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 100 } } }} rowSelectionModel={selectionModel} onRowSelectionModelChange={setSelectionModel} sx={{ ...harmonateDataGridSx, flex: 1, minHeight: 0, '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' } }} />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add GL Account Master' : 'Edit GL Account Master'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" required label="GL Code" value={form.glCode} onChange={(e) => setForm((p) => ({ ...p, glCode: e.target.value }))} />
            <TextField fullWidth size="small" required label="GL Description" value={form.glDescription} onChange={(e) => setForm((p) => ({ ...p, glDescription: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" required label="GL Account" value={form.glAccount} onChange={(e) => setForm((p) => ({ ...p, glAccount: e.target.value }))} />
            <TextField fullWidth size="small" required label="GL System" value={form.glSystem} onChange={(e) => setForm((p) => ({ ...p, glSystem: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <TextField fullWidth size="small" label="Account Type" value={form.accountType} onChange={(e) => setForm((p) => ({ ...p, accountType: e.target.value }))} />
            <Autocomplete
              fullWidth
              size="small"
              options={properties}
              value={selectedFormProperty}
              onChange={(_, value) => setForm((p) => ({ ...p, propertyId: getPropertyOptionId(value) || '' }))}
              getOptionLabel={getPropertyOptionLabel}
              isOptionEqualToValue={(option, value) => String(getPropertyOptionId(option)) === String(getPropertyOptionId(value))}
              ListboxProps={{ style: { maxHeight: 280 } }}
              renderOption={(props, option) => (
                <li {...props} key={getPropertyOptionId(option)}>
                  {getPropertyOptionLabel(option)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Business Unit"
                  placeholder="Select your business unit (optional)"
                  helperText={
                    properties.length
                      ? 'Optional — leave blank for enterprise-wide GL codes'
                      : 'No business units assigned to your user'
                  }
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    ...harmonateInputLabelProps,
                    shrink: true,
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={{
                width: 'calc(50% - 8px)',
                flexShrink: 0,
              }}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => !importLoading && resetImportState()} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          File Upload
          <IconButton size="small" onClick={resetImportState} disabled={importLoading} aria-label="Close" sx={harmonateDialogCloseIconSx}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <Autocomplete
            size="small"
            options={properties}
            value={selectedImportProperty}
            onChange={(_, value) => {
              setImportPropertyId(getPropertyOptionId(value) || '');
              clearSelectedImportFile();
            }}
            getOptionLabel={getPropertyOptionLabel}
            isOptionEqualToValue={(option, value) => String(getPropertyOptionId(option)) === String(getPropertyOptionId(value))}
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderOption={(props, option) => (
              <li {...props} key={getPropertyOptionId(option)}>
                {getPropertyOptionLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Business Unit"
                placeholder="Select your business unit (optional)"
                helperText={
                  properties.length
                    ? 'Optional — leave blank for enterprise-wide GL codes'
                    : 'No business units assigned to your user'
                }
                InputLabelProps={{
                  ...params.InputLabelProps,
                  ...harmonateInputLabelProps,
                  shrink: true,
                }}
              />
            )}
          />

          <Box
            onClick={() => {
              if (!importLoading) fileInputRef.current?.click();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!importLoading) setImportDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setImportDragActive(false);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={handleImportDrop}
            sx={{
              border: '2px dashed',
              borderColor: importDragActive ? ht.blue : ht.line,
              borderRadius: ht.fieldRadius,
              bgcolor: importDragActive ? 'rgba(46, 166, 160, 0.08)' : ht.listBg,
              p: 4,
              textAlign: 'center',
              cursor: importLoading ? 'default' : 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: importLoading ? ht.listBg : 'rgba(1, 48, 98, 0.04)',
                borderColor: ht.darkBlue,
              },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: importDragActive ? ht.blue : ht.fadedText, mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: ht.text }}>
              Drag your file(s) to start uploading
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: ht.text }}>
              OR
            </Typography>
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
              disabled={importLoading}
              sx={harmonateUploadOutlinedSx}
            >
              Browse files
            </Button>
            <Typography variant="body2" sx={{ mt: 1.5, color: ht.fadedText }}>
              Supports .csv, .xlsx and .xls
            </Typography>
          </Box>

          {importFileName ? (
            <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: ht.line, borderRadius: ht.fieldRadius, boxShadow: 'none' }}>
              <InsertDriveFileIcon sx={{ color: ht.text }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', color: ht.text }}>
                  {importFileName}
                </Typography>
                <Typography variant="caption" sx={{ color: ht.text }}>
                  {importReadyRows.length
                    ? `${importReadyRows.length} row(s) ready${
                        selectedImportProperty
                          ? ` for ${getPropertyOptionLabel(selectedImportProperty)}`
                          : ' (enterprise-wide)'
                      }`
                    : 'No valid rows found'}
                </Typography>
              </Box>
              <IconButton size="small" onClick={clearSelectedImportFile} disabled={importLoading} aria-label="Remove file" sx={{ color: ht.text }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ) : null}

          <Alert severity="info">
            Business Unit is optional. Leave it blank to import enterprise-wide GL codes, or select one to scope the import.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={resetImportState}
            disabled={importLoading}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={importLoading || !importReadyRows.length}
            sx={harmonateProceedButtonSx}
          >
            {importLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}><Typography sx={{ color: ht.text }}>Are you sure you want to delete &quot;{rowPendingDelete?.glDescription || rowPendingDelete?.glCode || 'selected'}&quot;?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default GlAccountMaster;
